import { useEffect, useRef, useState } from 'react';
import { epochKeyRing, fetchAndCacheEpochKey, SocketEvents } from '@clinikchat/shared';
import api from '../lib/api';
import { getSocket, useSocketStore } from '../lib/socket';
import { useCryptoStore } from '../stores/cryptoStore';

/** Waits before asking again for an epoch the server had no key (or no answer) for. */
export const BACKFILL_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000, 60_000];
/** After this many failed tries an epoch is left alone until a key-sharing event arrives. */
export const MAX_BACKFILL_ATTEMPTS = BACKFILL_RETRY_DELAYS_MS.length + 1;
/** Requests in flight at once, across every pass of one hook instance. */
export const MAX_PARALLEL = 4;
/**
 * Key events and reconnects reset the back-off so freshly shared keys are picked
 * up quickly, but a burst of them (a rotation, several joins, a flapping socket)
 * must not re-request every missing epoch each time: at most one reset per window.
 */
export const RESET_DEBOUNCE_MS = 10_000;

interface Attempt {
  count: number;
  nextAt: number;
}

/**
 * Fetch the channel key of every epoch the loaded messages were sent under.
 *
 * The device normally syncs only the CURRENT epoch, so anything sent under an
 * older one (history shared with a new member, or messages from before a
 * rotation) stayed undecryptable after a reload. Missing epochs are fetched with
 * a bounded number of requests in flight. An epoch the server has no key for yet
 * (a joiner who opens the channel before history sharing finishes) is retried with
 * growing delays, and again when the server announces a key event for this
 * channel or the socket reconnects (debounced). The returned counter changes
 * whenever new keys landed so the caller re-runs decryption, even if the effect
 * that started the fetch has since been replaced. Keys live only in the in-memory
 * epoch key ring.
 */
export function useEpochBackfill(channelId: string | undefined, epochs: readonly number[]): number {
  const secretKey = useCryptoStore((s) => s.secretKey);
  const socketId = useSocketStore((s) => s.socketId);
  const [version, setVersion] = useState(0);
  const [kick, setKick] = useState(0);
  const mounted = useRef(true);
  const attempts = useRef(new Map<string, Attempt>());
  const inFlight = useRef(new Set<string>());
  /**
   * Epochs whose key this hook has already told the caller about (by bumping the
   * version). A key that appears in the ring WITHOUT us fetching it (another sync
   * cached it) is not in here, so the next pass notices it and bumps too.
   */
  const accounted = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastReset = useRef(0);
  const attachedChannel = useRef<string | undefined>(undefined);
  const active = useRef(0);
  const waiters = useRef<Array<() => void>>([]);
  const wanted = epochs.join(',');
  // Latest inputs for timers and passes, which run outside any render. Written in an
  // effect, not during render (a discarded concurrent render must not leave a stale
  // value behind); declared before the effects that read them so it runs first.
  const channelRef = useRef(channelId);
  const wantedRef = useRef(wanted);
  useEffect(() => {
    channelRef.current = channelId;
    wantedRef.current = wanted;
  });

  /** One slot of MAX_PARALLEL, shared by every pass of this hook instance. */
  const acquire = (): Promise<void> => {
    if (active.current < MAX_PARALLEL) {
      active.current += 1;
      return Promise.resolve();
    }
    return new Promise((resolve) => waiters.current.push(resolve));
  };
  const release = () => {
    const next = waiters.current.shift();
    if (next) next();
    else active.current -= 1;
  };

  /**
   * Arm ONE timer for the soonest epoch (of the channel on screen NOW) still
   * waiting out a back-off. Called after every pass, and when a pass finds
   * nothing due, so a success that only covered some epochs, or a switch to
   * another channel, can never leave the rest without a scheduled retry.
   */
  const scheduleNext = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const channel = channelRef.current;
    if (!channel || !mounted.current) return;
    let soonest = Number.POSITIVE_INFINITY;
    for (const epoch of wantedRef.current ? wantedRef.current.split(',').map(Number) : []) {
      const id = `${channel}:${epoch}`;
      if (epochKeyRing.get(channel, epoch) || inFlight.current.has(id)) continue;
      const attempt = attempts.current.get(id);
      if (attempt && attempt.count < MAX_BACKFILL_ATTEMPTS) {
        soonest = Math.min(soonest, attempt.nextAt);
      }
    }
    if (soonest === Number.POSITIVE_INFINITY) return;
    timer.current = setTimeout(
      () => {
        if (mounted.current) setKick((k) => k + 1);
      },
      Math.max(0, soonest - Date.now()),
    );
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  // Forget past failures for this channel and retry. Runs when the channel is
  // attached (at once), on a reconnect, and on every key event for this channel
  // (both debounced: see RESET_DEBOUNCE_MS).
  useEffect(() => {
    if (!channelId) {
      attachedChannel.current = undefined;
      return;
    }
    const resetNow = () => {
      lastReset.current = Date.now();
      for (const key of [...attempts.current.keys()]) {
        if (key.startsWith(`${channelId}:`)) attempts.current.delete(key);
      }
      setKick((k) => k + 1);
    };
    const requestReset = () => {
      const wait = lastReset.current + RESET_DEBOUNCE_MS - Date.now();
      if (wait <= 0) {
        resetNow();
        return;
      }
      // Inside the window: one trailing reset, so the LAST event of a burst still counts.
      if (resetTimer.current) return;
      resetTimer.current = setTimeout(() => {
        resetTimer.current = null;
        if (mounted.current) resetNow();
      }, wait);
    };
    // A different channel starts fresh at once. A replaced socket (same channel) is
    // debounced like any other reset: a flapping connection must not re-request every
    // missing epoch each time.
    if (attachedChannel.current !== channelId) {
      attachedChannel.current = channelId;
      resetNow();
    } else {
      requestReset();
    }
    const socket = getSocket();
    if (!socket) return;
    const onKeyEvent = (payload: { channelId?: string }) => {
      if (payload?.channelId !== channelId) return;
      requestReset();
    };
    // socket.io keeps the same socket object across an ordinary reconnect, so the
    // socketId dependency alone would miss it, and a key event sent while offline is lost.
    const onConnect = () => requestReset();
    socket.on(SocketEvents.CHANNEL_KEY_ROTATED, onKeyEvent);
    socket.on('connect', onConnect);
    return () => {
      socket.off(SocketEvents.CHANNEL_KEY_ROTATED, onKeyEvent);
      socket.off('connect', onConnect);
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
        resetTimer.current = null;
      }
    };
  }, [channelId, socketId]);

  // A key written to the ring by any other path (the sidebar sharing history while
  // this channel is open, the current-epoch sync) produces no socket event on this
  // device, so subscribe at the write boundary and tell the caller to re-decrypt.
  useEffect(() => {
    if (!channelId) return;
    return epochKeyRing.onSet((channel, epoch) => {
      if (channel !== channelId || !mounted.current) return;
      if (!wantedRef.current.split(',').includes(String(epoch))) return;
      // Our own fetch: the pass announces its whole batch with ONE bump when it ends.
      if (inFlight.current.has(`${channel}:${epoch}`)) return;
      accounted.current.add(`${channel}:${epoch}`);
      setVersion((v) => v + 1);
    });
  }, [channelId]);

  useEffect(() => {
    if (!channelId || !secretKey || !wanted) return;
    const now = Date.now();
    // Keys that reached the ring by another route (the current-epoch sync, the key
    // rotation panel, a sibling hook) since the list was last decrypted: the caller's
    // memo does not watch the ring, so without a bump those messages stay placeholders.
    let landedElsewhere = false;
    for (const epoch of wanted.split(',').map(Number)) {
      const id = `${channelId}:${epoch}`;
      if (epochKeyRing.get(channelId, epoch) && !accounted.current.has(id)) {
        accounted.current.add(id);
        landedElsewhere = true;
      }
    }
    if (landedElsewhere) setVersion((v) => v + 1);
    const missing = wanted
      .split(',')
      .map(Number)
      .filter((epoch) => {
        const id = `${channelId}:${epoch}`;
        if (epochKeyRing.get(channelId, epoch) || inFlight.current.has(id)) return false;
        const attempt = attempts.current.get(id);
        if (!attempt) return true;
        return attempt.count < MAX_BACKFILL_ATTEMPTS && now >= attempt.nextAt;
      });
    if (missing.length === 0) {
      scheduleNext();
      return;
    }

    // Reserve EVERYTHING queued up front: an overlapping pass (mount-time kick, the
    // epoch list changing while scrolling, StrictMode) must not fetch the same epoch
    // again while it waits its turn.
    for (const epoch of missing) inFlight.current.add(`${channelId}:${epoch}`);

    // Deliberately not cancelled when this effect is replaced: the fetch is shared
    // work, and its result must still reach the mounted hook.
    void (async () => {
      let gained = false;
      const queue = [...missing];
      const worker = async () => {
        for (;;) {
          const epoch = queue.shift();
          if (epoch === undefined) return;
          const id = `${channelId}:${epoch}`;
          await acquire();
          // Left the channel (or unmounted) while queued: drop the rest of the queue
          // instead of competing with the channel now on screen. A pass for the SAME
          // channel started elsewhere keeps its own queue.
          if (!mounted.current || channelRef.current !== channelId) {
            inFlight.current.delete(id);
            release();
            for (const rest of queue.splice(0)) inFlight.current.delete(`${channelId}:${rest}`);
            return;
          }
          try {
            const result = await fetchAndCacheEpochKey({ api, secretKey }, channelId, epoch);
            if (result.status === 'success') {
              attempts.current.delete(id);
              accounted.current.add(id);
              gained = true;
            } else if (epochKeyRing.get(channelId, epoch)) {
              // Our fetch failed but another path cached the key meanwhile (the ring
              // listener skipped it because this fetch was in flight): still announce it.
              attempts.current.delete(id);
              accounted.current.add(id);
              gained = true;
            } else {
              const count = (attempts.current.get(id)?.count ?? 0) + 1;
              const delay =
                BACKFILL_RETRY_DELAYS_MS[Math.min(count - 1, BACKFILL_RETRY_DELAYS_MS.length - 1)]!;
              attempts.current.set(id, { count, nextAt: Date.now() + delay });
            }
          } finally {
            inFlight.current.delete(id);
            release();
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(MAX_PARALLEL, missing.length) }, () => worker()),
      );
      if (!mounted.current) return;
      if (gained) setVersion((v) => v + 1);
      scheduleNext();
    })();
  }, [channelId, secretKey, wanted, kick]);

  return version;
}
